'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationType =
  | 'booking' | 'cancellation' | 'reschedule' | 'payment' | 'time_off' | 'queue' | 'no_show';

export interface AppNotification {
  id: string;
  duplicateIds?: string[];
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  meta?: {
    customerName?: string;
    barberName?: string;
    service?: string;
    amount?: number;
    appointmentId?: string;
    waitlistId?: string;
  };
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  markAllRead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
  addNotification: (n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function rowToNotification(row: Record<string, any>): AppNotification {
  return {
    id: row.id,
    type: (row.type as NotificationType) || 'booking',
    title: row.title || '',
    message: row.message || '',
    timestamp: new Date(row.created_at),
    read: row.is_read ?? false,
    meta: row.meta ?? undefined,
  };
}

function notificationDedupeKey(notification: AppNotification): string {
  const entityId = notification.meta?.appointmentId ?? notification.meta?.waitlistId;
  if (entityId) return `${notification.type}:${entityId}`;
  return [
    notification.type,
    notification.title,
    notification.message,
    notification.timestamp.getTime(),
  ].join(':');
}

function normalizeNotifications(rows: Record<string, any>[]): AppNotification[] {
  const sorted = rows
    .map(rowToNotification)
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const byKey = new Map<string, AppNotification>();

  for (const notification of sorted) {
    const key = notificationDedupeKey(notification);
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, { ...notification, duplicateIds: [] });
      continue;
    }

    existing.duplicateIds = [...(existing.duplicateIds ?? []), notification.id, ...(notification.duplicateIds ?? [])];
    existing.read = existing.read && notification.read;
  }

  return Array.from(byKey.values());
}

// ─── Context ──────────────────────────────────────────────────────────────────

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const supabase = useMemo(() => createClient(), []);
  const { shopOwnerId } = useAuth();

  // ── Fetch from Supabase ──────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const notificationOwnerId = shopOwnerId ?? user.id;

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', notificationOwnerId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.log('Notifications fetch error:', error.message);
        return;
      }

      setNotifications(normalizeNotifications(data || []));
    } catch {
      // silently ignore network errors
    }
  }, [supabase, shopOwnerId]);

  // ── Poll every 10 seconds ────────────────────────────────────────────────
  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 10000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // ── Mark single read ─────────────────────────────────────────────────────
  const markRead = useCallback(async (id: string) => {
    const ids = notifications.find((n) => n.id === id)?.duplicateIds ?? [];
    const idsToUpdate = [id, ...ids];
    setNotifications((prev) =>
      prev.map((n) => (idsToUpdate.includes(n.id) ? { ...n, read: true } : n))
    );
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .in('id', idsToUpdate);
    } catch {
      // optimistic update already applied
    }
  }, [supabase, notifications]);

  // ── Mark all read ────────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const notificationOwnerId = shopOwnerId ?? user.id;
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', notificationOwnerId)
        .eq('is_read', false);
    } catch {
      // optimistic update already applied
    }
  }, [supabase, shopOwnerId]);

  // ── Dismiss (delete) ─────────────────────────────────────────────────────
  const dismiss = useCallback(async (id: string) => {
    const ids = notifications.find((n) => n.id === id)?.duplicateIds ?? [];
    const idsToDelete = [id, ...ids];
    setNotifications((prev) => prev.filter((n) => !idsToDelete.includes(n.id)));
    try {
      await supabase
        .from('notifications')
        .delete()
        .in('id', idsToDelete);
    } catch {
      // optimistic update already applied
    }
  }, [supabase, notifications]);

  // ── Clear all ────────────────────────────────────────────────────────────
  const clearAll = useCallback(async () => {
    setNotifications([]);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const notificationOwnerId = shopOwnerId ?? user.id;
      await supabase
        .from('notifications')
        .delete()
        .eq('user_id', notificationOwnerId);
    } catch {
      // optimistic update already applied
    }
  }, [supabase, shopOwnerId]);

  // ── Manual add (for external callers) ───────────────────────────────────
  const addNotification = useCallback(
    async (n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const notificationOwnerId = shopOwnerId ?? user.id;
        await supabase.from('notifications').insert({
          user_id: notificationOwnerId,
          type: n.type,
          title: n.title,
          message: n.message,
          is_read: false,
          meta: n.meta ?? {},
        });
        // Next poll will pick it up; trigger immediate refresh
        await fetchNotifications();
      } catch {
        // silently ignore
      }
    },
    [supabase, fetchNotifications, shopOwnerId]
  );

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, markAllRead, markRead, dismiss, clearAll, addNotification }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationProvider');
  return ctx;
}
